import type {
	OrderCatalogSku,
	OrderServiceMode,
	PackagingOption,
	PortalOrder,
	PortalOrderDraft,
	PortalOrderPatch,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import {
	collectedOnSite,
	emptyOrderCards,
	type OrderCardsValue,
	orderCardsDraft,
	usesStock,
	validateOrderCards,
} from "@khazzin/ui/patterns";
import type { FormErrors } from "@khazzin/ui/patterns/form";

/**
 * The Store Portal's New Order form, as values (EP10-S05, DA-10-04): the
 * request it becomes, the order it is read back from, the preview totals and
 * the checks it can make without asking.
 *
 * **Pure.** Everything here is a function of the values, so the arithmetic and
 * the mapping are tested without a screen.
 *
 * **The form sends no price.** Unit prices are the catalogue's and packaging
 * prices the store's terms, both resolved by the server on submit; the totals
 * below are a preview, and the confirmation shows the server's.
 */

export interface DraftLine {
	rowId: string;
	sku: string;
	name: string;
	qty: number;
	/** The price the picker showed, for the preview only. */
	unitPriceMinor: number;
	/** The per-item package; empty takes the order's (ADJ-1022-2). */
	packageTypeId: string;
	/** What the shelf holds, or null when nothing can say. */
	available: number | null;
}

export interface DraftValues {
	customerName: string;
	customerPhone: string;
	/** The type boxes (DA-10-04): cash, fragile and urgent here; the order types below. */
	cod: boolean;
	fragile: boolean;
	urgent: boolean;
	/** The service mode and the order-type boxes with their answers (ADJ-1022-3). */
	orderCards: OrderCardsValue;
	cityId: string;
	cityName: string;
	/** Filled from the city, never typed (CR-001). */
	provinceId: string;
	provinceName: string;
	addressLine: string;
	notes: string;
	codAmountMinor: number | null;
	lines: DraftLine[];
	/** One package for the whole order instead of one per item. */
	combine: boolean;
	combinedPackageTypeId: string;
	/**
	 * The order's package (ADJ-1022-2): each item naming none is packed in
	 * it. Empty leaves them to the store's default.
	 */
	packageTypeId: string;
	[key: string]: unknown;
}

/** The Lebanese prefix the phone opens with. */
export const PHONE_PREFIX = "+961";

/** The tag the Fragile box writes. */
export const FRAGILE = "fragile";

export function emptyDraft(serviceMode: OrderServiceMode = "fulfillment_delivery"): DraftValues {
	return {
		customerName: "",
		customerPhone: PHONE_PREFIX,
		cod: true,
		orderCards: emptyOrderCards(serviceMode),
		fragile: false,
		urgent: false,
		cityId: "",
		cityName: "",
		provinceId: "",
		provinceName: "",
		addressLine: "",
		notes: "",
		codAmountMinor: null,
		lines: [],
		combine: false,
		combinedPackageTypeId: "",
		packageTypeId: "",
	};
}

let rows = 0;
function rowId(): string {
	rows += 1;
	return `row-${rows}`;
}

/** A line from the picker's row, one unit, in the store's default package. */
export function lineOf(sku: OrderCatalogSku): DraftLine {
	return {
		rowId: rowId(),
		sku: sku.skuCode,
		name: sku.name,
		qty: 1,
		unitPriceMinor: sku.retailPriceMinor ?? 0,
		packageTypeId: "",
		available: sku.available ?? null,
	};
}

/**
 * The lines with one more of a picked SKU (ADJ-M03-4): a SKU already on the
 * order gains a unit rather than a second line, which the server would read
 * as two separately packed items.
 */
export function addSku(lines: readonly DraftLine[], sku: OrderCatalogSku): DraftLine[] {
	const at = lines.findIndex((line) => line.sku === sku.skuCode);
	if (at === -1) {
		return [...lines, lineOf(sku)];
	}
	return lines.map((line, index) => (index === at ? { ...line, qty: line.qty + 1 } : line));
}

/**
 * The package picker's "Store default" choice. A select cannot carry an empty
 * value, so the line's empty `packageTypeId` is this on screen (ADJ-M03-4).
 */
export const STORE_DEFAULT = "store-default";

/** The form an existing order opens in — to edit it, or to resubmit it. */
export function draftOf(order: PortalOrder): DraftValues {
	return {
		...emptyDraft(),
		customerName: order.order.customerName,
		customerPhone: order.customerPhone,
		cod: order.paymentMethod === "cod",
		orderCards: {
			...emptyOrderCards(order.serviceMode),
			cards: [...order.cards],
			handoff: order.handoff ?? "store_drops",
			pickupWarehouseId: order.pickupWarehouseId ?? "",
			pickupInstructions: order.pickupInstructions ?? "",
			exchangeNotes: order.exchangeNotes ?? "",
			expectedReturns: order.expectedReturns.map((row, index) => ({
				rowId: `xr-${index}`,
				sku: row.sku,
				name: row.title ?? row.sku,
				qty: row.qty,
				notes: row.notes ?? "",
			})),
			pickupDetails: order.pickupDetails ?? "",
			designRefundMinor: order.designRefundMinor > 0 ? order.designRefundMinor : null,
		},
		fragile: order.tags.includes(FRAGILE),
		urgent: order.order.urgency === "urgent",
		cityId: order.cityId ?? "",
		cityName: order.order.cityName ?? "",
		provinceId: order.provinceId ?? "",
		provinceName: order.order.provinceName ?? "",
		addressLine: order.addressLine ?? "",
		notes: order.notes ?? "",
		codAmountMinor: order.paymentMethod === "cod" ? order.codAmountMinor : null,
		lines: order.lines.map((line) => ({
			rowId: rowId(),
			sku: line.sku,
			name: line.title ?? line.sku,
			qty: line.qtyOrdered,
			unitPriceMinor: line.unitPriceMinor,
			packageTypeId: line.packageTypeId ?? "",
			available: null,
		})),
	};
}

function tagsOf(values: DraftValues): string[] {
	return values.fragile ? [FRAGILE] : [];
}

/** The create (or resubmit) request. Absent rather than empty for anything optional. */
export function toPortalDraft(values: DraftValues): PortalOrderDraft {
	const packed = usesStock(values.orderCards);
	const draft: PortalOrderDraft = {
		...orderCardsDraft(values.orderCards),
		urgency: values.urgent ? "urgent" : "normal",
		customerPhone: values.customerPhone.trim(),
		tags: tagsOf(values),
		paymentMethod: values.cod ? "cod" : "prepaid",
		codAmountMinor: values.cod ? (values.codAmountMinor ?? 0) : 0,
		lines: values.lines.map((line) =>
			values.combine || line.packageTypeId === "" || !packed
				? { sku: line.sku, qty: line.qty }
				: { sku: line.sku, qty: line.qty, packageTypeId: line.packageTypeId },
		),
	};
	// A pickup is collected at our warehouse: no address (ADJ-1022-3).
	if (!collectedOnSite(values.orderCards)) {
		draft.provinceId = values.provinceId;
		draft.cityId = values.cityId;
		draft.addressLine = values.addressLine.trim();
	}
	if (values.customerName.trim() !== "") {
		draft.customerName = values.customerName.trim();
	}
	if (values.notes.trim() !== "") {
		draft.notes = values.notes.trim();
	}
	if (packed && !values.combine && values.packageTypeId !== "") {
		draft.packageTypeId = values.packageTypeId;
	}
	if (packed && values.combine && values.combinedPackageTypeId !== "" && values.lines.length > 0) {
		draft.combinedPackages = [
			{
				packageTypeId: values.combinedPackageTypeId,
				lines: values.lines.map((_, index) => index + 1),
			},
		];
	}
	return draft;
}

/** The edit of a pending order: what a store may correct, never the items or the type. */
export function toPatch(values: DraftValues): PortalOrderPatch {
	return {
		customerName: values.customerName.trim(),
		customerPhone: values.customerPhone.trim(),
		provinceId: values.provinceId,
		cityId: values.cityId,
		addressLine: values.addressLine.trim(),
		notes: values.notes.trim(),
		urgency: values.urgent ? "urgent" : "normal",
		paymentMethod: values.cod ? "cod" : "prepaid",
		codAmountMinor: values.cod ? (values.codAmountMinor ?? 0) : 0,
		tags: tagsOf(values),
	};
}

/** What one unit of a line is charged for its own package; a per-package type bills the order once. */
function perItem(option: PackagingOption | undefined): number {
	return option === undefined || option.priceUnit === "per_package" ? 0 : option.priceMinor;
}

export interface Totals {
	goodsMinor: number;
	packagingMinor: number;
	/** What the driver collects: the COD amount, or nothing when prepaid. */
	toCollectMinor: number;
}

/** The footer's preview. The confirmation shows the server's own. */
export function previewTotals(values: DraftValues, packaging: readonly PackagingOption[]): Totals {
	// A parcel the store packed, or a design pickup, bills no packaging of ours.
	const offered = usesStock(values.orderCards) ? packaging : [];
	const byId = new Map(offered.map((option) => [option.packageTypeId, option]));
	const storeDefault = offered.find((option) => option.isStoreDefault);
	// An item naming no package goes in the order's, else the store's default.
	const orderPackage = byId.get(values.packageTypeId) ?? storeDefault;
	const goodsMinor = values.lines.reduce((sum, line) => sum + line.qty * line.unitPriceMinor, 0);
	let packagingMinor = values.lines.reduce(
		(sum, line) => sum + line.qty * perItem(byId.get(line.packageTypeId) ?? orderPackage),
		0,
	);
	if (values.combine) {
		// The lines then carry the store's default, and the one package is
		// charged once on top — the server's reading of combined packaging.
		packagingMinor =
			values.lines.reduce((sum, line) => sum + line.qty * perItem(storeDefault), 0) +
			(byId.get(values.combinedPackageTypeId)?.priceMinor ?? 0);
	}
	return {
		goodsMinor,
		packagingMinor,
		toCollectMinor: values.cod ? (values.codAmountMinor ?? 0) : 0,
	};
}

/**
 * The checks worth a round trip saved. The server makes every one again and
 * wins; its refusal lands under the same input, because the keys are the
 * wire's names (`lines[2].qty`).
 */
export function validateDraft(values: DraftValues, t: Translate, withLines = true): FormErrors {
	const errors: FormErrors = {};
	const phone = values.customerPhone.trim();
	if (phone === "" || phone === PHONE_PREFIX) {
		errors.customerPhone = t("portal.newOrder.errors.phone");
	}
	if (!collectedOnSite(values.orderCards)) {
		if (values.cityId === "") {
			errors.cityId = t("portal.newOrder.errors.city");
		}
		if (values.addressLine.trim() === "") {
			errors.addressLine = t("portal.newOrder.errors.address");
		}
	}
	if (values.cod && (values.codAmountMinor ?? 0) <= 0) {
		errors.codAmountMinor = t("portal.newOrder.errors.cod");
	}
	if (!withLines) {
		return errors;
	}
	Object.assign(errors, validateOrderCards(values.orderCards, t));
	const packed = usesStock(values.orderCards);
	if (values.lines.length === 0 && packed) {
		// A delivery-only parcel or a design pickup may list nothing.
		errors.lines = t("portal.newOrder.errors.lines");
	}
	if (packed && values.combine && values.combinedPackageTypeId === "") {
		errors.combinedPackageTypeId = t("portal.newOrder.errors.combined");
	}
	values.lines.forEach((line, index) => {
		if (!Number.isInteger(line.qty) || line.qty < 1) {
			errors[`lines[${index}].qty`] = t("portal.newOrder.errors.qty");
		} else if (packed && line.available !== null && line.qty > line.available) {
			errors[`lines[${index}].qty`] = t("portal.newOrder.errors.overAvailable", {
				available: line.available,
			});
		}
	});
	return errors;
}

/** Where a store's unsent form is kept, per store, until it is submitted. */
export function draftKey(storeId: string): string {
	return `khazzin.portal.newOrder.${storeId}`;
}

/** The unsent form, or null. Storage can be denied, so it is best effort. */
export function loadDraft(storeId: string): DraftValues | null {
	try {
		const raw = window.sessionStorage.getItem(draftKey(storeId));
		return raw === null ? null : { ...emptyDraft(), ...(JSON.parse(raw) as Partial<DraftValues>) };
	} catch {
		return null;
	}
}

export function saveDraft(storeId: string, values: DraftValues | null) {
	try {
		if (values === null) {
			window.sessionStorage.removeItem(draftKey(storeId));
		} else {
			window.sessionStorage.setItem(draftKey(storeId), JSON.stringify(values));
		}
	} catch {
		// A private window: the draft lasts as long as the screen.
	}
}
